import type { CampaignSendProgress as Progress } from '@peles/shared';

export interface CampaignSendProgressProps {
  progress?: Progress;
  /** Still going out: the bar moves, the words say so. */
  active: boolean;
}

/** "Sending… 34 of 120" — how far a send has got, as the page follows it. */
export function CampaignSendProgress({ progress, active }: CampaignSendProgressProps) {
  if (!progress) {
    return active ? <p className="text-sm text-gray-600">Starting to send…</p> : null;
  }
  const { reached, failed, waiting, total } = progress;
  const done = reached + failed;
  const percent = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="space-y-1.5" aria-live="polite">
      <div className="flex justify-between text-sm">
        <span className="font-medium text-gray-900">
          {active ? `Sending… ${done} of ${total}` : `Sent to ${reached} of ${total}`}
        </span>
        <span className="text-gray-500">{percent}%</span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-label="Invitations sent"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
      >
        <div
          className="h-full rounded-full bg-primary-600 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-xs text-gray-500">
        {failed > 0 && <span className="text-red-600">{failed} could not be sent · </span>}
        {waiting > 0 ? `${waiting} still to go` : 'Nobody left to send to'}
      </p>
    </div>
  );
}
